<template>
  <div class="typer" @keyup.esc="closePicker" tabindex="0">
    <div :class="['typerContent',
      {
        typerContentActive: !showSubmit,
        typerContentEmoji: !isUserAuthenticated && getChannel !== 'KUPOAPP_HN'
      }]
    ">
      <div
        class="submit"
        v-if="!showAiaiBotInput &&
          (isUserAuthenticated || getChannel === 'KUPOAPP_HN')"
      >
        <ChatButton
          :types="['round']"
          :size="30"
          @click="$refs.fileInput.click()"
        >
          <Icon
            class="typer-attachment"
            :art="'light'"
            :height="16"
            name="paperclip"
            color="gray"
          />
        </ChatButton>
        <input
          v-show="false"
          type="file"
          id="webFile"
          @change="upload"
          ref="fileInput"
        >
      </div>
      <div v-if="showEmojis" class="emojiPicker">
        <ChatButton
          :types="['round']"
          :size="24"
          class="trigger"
        >
          <Icon
            :art="'light'"
            :name="'face-laugh'"
            :color="'gray'"
            :height="20"
            :width="20"
          />
        </ChatButton>
      </div>
      <textarea-autosize
        id="chatInputField"
        :class="['chatField', {
          'padding-left': inputEmail || inputPhoneNumber
        }]"
        name="input-1"
        :disabled="disableSendButton"
        :placeholder="inputPlaceholder"
        @click.native="closePicker()"
        @keydown.native="newLineKey"
        @keyup.enter="submit"
        v-bind:value="content"
        v-on:input.native="content = $event.target.value"
        :max-height="100"
        :maxlength="messageMaxLength"
        rows="1"
      />
    </div>
    <transition name="fade">
      <div class="submit" v-show="showSubmit">
        <ChatButton
          :types="['round']"
          :size="24"
          class="submit-button"
          :disabled="noTypedMessage || disableSendButton"
          @click="submit"
        >
          <img src="@/assets/icons/send-horizontal.svg" class="msg-submit" />
        </ChatButton>
      </div>
    </transition>
  </div>
</template>

<script>
import EmojiPicker from '@/helpers/emojis'
import Icon from './Icon'
import ChatButton from './ChatButton'
import { mapActions, mapGetters, mapState } from 'vuex'
import { throttle } from 'lodash'
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { validateEmail, validatePhoneNumber } from '@/helpers/validations'

const MSG_MAX_LENGTH = process.env.VUE_APP_MSG_MAX_LENGTH
let self
const EMAIL = 'email'
const PHONE = 'phone'

export default {
  name: 'Typer',
  components: {
    ChatButton,
    Icon
  },
  props: {
    placeholder: String,
    emojis: Boolean,
    icon: {
      type: String,
      default: 'link'
    },
    rules: Array
  },

  setup () {
    const { $label: t } = useI36n()
    return { t }
  },

  data () {
    return {
      content: '',
      moodIcon: 'mood',
      showSubmit: false,
      shiftKey: false
    }
  },
  computed: {
    ...mapState('conversation', ['disableSendButton', 'showAiaiBotInput', 'aiaiBotInputConfig']),
    ...mapState('uistate', ['fullScreen']),
    ...mapGetters('conversation', [
      'isUserAuthenticated',
      'getChannel'
    ]),
    noTypedMessage () {
      return this.content.trim().length === 0
    },
    inputEmail () {
      return Object.keys(this.aiaiBotInputConfig).length
        && this.aiaiBotInputConfig.config.inputType === EMAIL
    },
    inputPhoneNumber () {
      return Object.keys(this.aiaiBotInputConfig).length
        && this.aiaiBotInputConfig.config.inputType === PHONE
    },
    inputPlaceholder () {
      if (this.inputEmail) {
        return `${this.t('transcript_placeholder')} ${this.t('email_placeholder')}`
      } else if (this.inputPhoneNumber) {
        return this.t('phone_placeholder')
      }
      return this.t('typer_placeholder')
    },
    chatBotEmojisSupport () {
      return Object.keys(this.aiaiBotInputConfig).length
        && ![EMAIL, PHONE].includes(this.aiaiBotInputConfig.config.inputType)
    },
    messageMaxLength () {
      const maxLength = Object.keys(this.aiaiBotInputConfig).length
        && this.aiaiBotInputConfig.config?.maxLength

      return maxLength || MSG_MAX_LENGTH
    },
    showEmojis () {
      return this.showAiaiBotInput ? this.chatBotEmojisSupport : this.emojis
    }
  },
  watch: {
    content () {
      this.contentChanged()
    }
  },
  mounted () {
    self = this
    if (this.emojis) {
      let emojiPickerContainer = document.getElementsByClassName('emojiPicker')
      
      EmojiPicker.addSupport(emojiPickerContainer[0], (event) => {
        self.content = self.content + event.target.innerHTML
        var chatInputField = document.getElementById('chatInputField')
        chatInputField.focus()
      })
    }
    this.$el.querySelector('.typer .typerContent #chatInputField.chatField').focus()
  },
  methods: {
    ...mapActions('conversation', [
      'addMessage',
      'sendMessage',
      'sendUserIsTyping',
      'sendUserIsNotTyping',
      'setShowAiaiBotInput',
      'resetAiaiBotInputConfig',
      'uploadFile'
    ]),
    closePicker () {
      EmojiPicker.closePicker()
    },
    upload (event) {
      const files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        console.warn('no files selected')
      } else if (!this.isAcceptedFileSize(files)) {
        this.addMessage({
          convId: -1,
          msgLabelKey: 'messages_acceptedFile',
          senderId: -2000,
          screenName: 'Sanitas',
          msgType: 'CHAT'
        })
      } else {
        this.uploadFile(files[0])
      }
    },
    isAcceptedFileSize (files) {
      let totalSize = 0
      Array.prototype.forEach.call(files, function (file) {
        totalSize += file.size
      })
      return totalSize / 1024 / 1024 < 5
    },
    newLineKey (event) {
      if (event.shiftKey) {
        this.shiftKey = true
      }
    },
    throttledMessage: throttle((callback, value) => {
      callback(value)
    }, 1000),
    validateChatBotIntent (content) {
      if (this.inputEmail) {
        return validateEmail(content)
      } else if (this.inputPhoneNumber) {
        return validatePhoneNumber(content)
      }

      return true
    },
    contentChanged () {
      let callback = this.sendUserIsNotTyping
      if (this.content.trim().length !== 0) {
        if (this.inputEmail || this.inputPhoneNumber) {
          this.showSubmit = this.validateChatBotIntent(this.content)
          this.shiftKey = false
        } else {
          let matchEnter = /\r|\n/.exec(this.content.slice(-1))
          if (matchEnter && !this.shiftKey) {
            this.submit()
          } else {
            callback = this.sendUserIsTyping
            this.showSubmit = true
            this.shiftKey = false
          }
        }
      } else {
        this.showSubmit = false
        this.shiftKey = false
      }
      this.throttledMessage(callback, this.content)
    },
    submit () {
      const message = this.showAiaiBotInput
        ? {
          content: this.content.substr(0, this.messageMaxLength).trim(),
          msgType: 'AIAIBOT_FEEDBACK',
          widgetType: this.aiaiBotInputConfig.widgetType
        }
        : this.content.substr(0, MSG_MAX_LENGTH).trim()

      if (this.content.trim().length !== 0) {
        this.sendMessage(message)
        this.$emit('scrollToEnd')
        EmojiPicker.closePicker()
        document.getElementById('chatInputField').value = ''
        this.content = ''
        this.showSubmit = false

        if (this.showAiaiBotInput) {
          this.setShowAiaiBotInput(false)
          this.resetAiaiBotInputConfig
        }
      }
    }
  }
}
</script>

<style scoped lang="postcss">
.typer {
  min-height: 60px;
  background-color: #004435;
  display: flex;
  flex: none;
  padding: 8px;
  box-sizing: border-box;

  .chatField:focus {
    outline: none;
  }

  .chatField {
    width: calc(100% - 35px);
    font-size: 16px;
    line-height: 1.5;
    margin: 6px 0 6px 10px;
    padding-right: 20px;
    border: none;
    height: 29px !important;
    min-height: auto;
    
    .popup & {
      margin: 6px 0;
    }

    &.padding-left {
      padding-left: 10px;
    }
  }

  .typerContent {
    display: flex;
    align-items: center;
    width: calc(100% - 30px);
    border-radius: 2px;
    background-color: white;

    .emojiPicker {
      position: relative;
      margin: 0 5px 0 8px;
    }
  }

  .typerContentActive {
    width: 100%;
    transform: translateX(0);
  }

  .submit {
    display: flex;
    align-items: center;
    width: 20px;

    .typer-attachment {
      transform: rotate(-45deg);
    }

    .msg-submit {
      margin: auto;
    }

    .submit-button {
      min-height: auto;
    }

    .upload {
      transform: rotate(-45deg);
      margin: 5px auto auto;
      height: 24px;
      width: 24px;
    }

    .submit-button.disabled {
      background-color: #004435;
    }
  }

  .submit+.typerContent, .typerContent+.submit {
    margin-left: 8px;
  }
}

.fade-enter-active {
  transition: all .5s ease;
}

.fade-enter, .fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
